import React, { useCallback, useLayoutEffect } from 'react';
import Animated from 'react-native-reanimated';
import { StyleSheet, View } from 'react-native';
import Text from 'design-system/Text';
import Squircle from 'design-system/Squircle';
import { AppView } from 'design-system/AppView';
import { CardView, Spacer } from 'design-system';
import IconStatusUp from 'design-system/icons/IconStatusUp';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import NavigationCloseButton from 'design-system/NavigationCloseButton';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { IconActivity, IconMoneyBill, IconShieldDone, IconUser } from 'design-system/icons';
import useColors from 'hooks/useColors';
import { useAppDispatch } from 'store/hooks';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { setDeviceSettingsKey } from 'actions/deviceSettings';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import Links from '../../../utils/links';
import { isWeb } from '../../../constants';
const MigrationToSecclIntro = withAnimatedNavigationHeader(({ navigation, scrollHandler }) => {
    const dispatch = useAppDispatch();
    const colors = useColors();
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const handleGoBack = useCallback(() => {
        navigation.pop();
        dispatch(setDeviceSettingsKey('didViewSecclMigrationIntro', true));
    }, []);
    const handleContinue = useCallback(() => {
        handleGoBack();
        navigation.navigateDeprecated('SecclMigrationExpectations');
    }, [handleGoBack]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerLeft: () => <NavigationCloseButton inWebHeader onPress={handleGoBack}/>,
            headerBackVisible: false,
        });
    }, [handleGoBack]);
    const handleLearnMore = useCallback(() => {
        Links.openLink('https://emma-app.com/learn-more/alpaca-to-seccl-invest-fees');
    }, []);
    return (<AppView withFrame={false}>
        <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} contentContainerStyle={paddingBottom} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader title="Keep investing with Emma" subTitle={'We’re transitioning our General investment account provider from Alpaca to Seccl, an FCA\u2011authorised provider.'} style={styles.screenInfoHeaderStyles}/>
          <View style={styles.paddingHorizontal}>
            <Text TextThin-16 Secondary>
              Moving your account gives you:
            </Text>
          </View>
          <CardView noMarginBottom>
            <View style={styles.row}>
              <View style={styles.flex}>
                <Text Text-16>Stronger protection</Text>
                <Spacer h={4}/>
                <Text TextThin-14 Secondary>
                  Your assets are held with an FCA-regulated custodian under CASS safeguarding rules. Cash may be
                  protected by the FSCS up to £120,000 if Seccl were to fail.
                </Text>
              </View>
              <Spacer w={16}/>
              <Squircle color={colors.background.dark}>
                <IconShieldDone colorKey="statusPositive"/>
              </Squircle>
            </View>

            <View style={styles.row}>
              <View style={styles.flex}>
                <Text Text-16>A new investment universe</Text>
                <Spacer h={4}/>
                <Text TextThin-14 Secondary>
                  We’re updating what you can invest in. As part of this move, we’re transitioning from individual
                  stocks to ETFs and investment funds, which offer broader market exposure.
                </Text>
              </View>
              <Spacer w={16}/>
              <Squircle color={colors.background.dark}>
                <IconActivity alt gradientKey="darkPink"/>
              </Squircle>
            </View>

            <View style={styles.row}>
              <View style={styles.flex}>
                <Text Text-16>Future investment features</Text>
                <Spacer h={4}/>
                <Text TextThin-14 Secondary>
                  This upgrade lays the groundwork for Stocks & Shares ISAs and additional investment options inside
                  Emma.
                </Text>
              </View>
              <Spacer w={16}/>
              <Squircle color={colors.background.dark}>
                <IconUser gradientKey="skyBlue"/>
              </Squircle>
            </View>

            <View style={styles.row}>
              <View style={styles.flex}>
                <Text Text-16>Industry-standard custody</Text>
                <Spacer h={4}/>
                <Text TextThin-14 Secondary>
                  Your investments will sit on a modern, regulated custody platform designed for UK investment products.
                </Text>
              </View>
              <Spacer w={16}/>
              <Squircle color={colors.background.dark}>
                <IconStatusUp gradientKey="yellow"/>
              </Squircle>
            </View>

            <View style={styles.row}>
              <View style={styles.flex}>
                <Text Text-16>Updated fees</Text>
                <Spacer h={4}/>
                <Text TextThin-14 Secondary>
                  As part of this upgrade, our General investment account fees are changing. The fee you pay will now be
                  based on the value of the investments in your account, and will depend on your subscription level.{' '}
                  <Text Text-14 Primary suppressHighlighting onPress={handleLearnMore}>
                    {'Learn\xa0more'}
                  </Text>
                </Text>
              </View>
              <Spacer w={16}/>
              <Squircle color={colors.background.dark}>
                <IconMoneyBill alt gradientKey="peach"/>
              </Squircle>
            </View>
          </CardView>
        </Animated.ScrollView>
        <Button floating brand title="Continue" onPress={handleContinue}/>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
export default MigrationToSecclIntro;
const styles = StyleSheet.create({
    flex: {
        flex: 1,
    },
    row: {
        padding: rem(16),
        flexDirection: 'row',
        alignItems: 'center',
    },
    screenInfoHeaderStyles: {
        paddingBottom: rem(10),
    },
    paddingHorizontal: {
        paddingHorizontal: rem(16),
    },
});
